/**
 * usePanelUIState Hook
 *
 * Manages the UI state of the Solomon panel (collapsed/expanded, position).
 * Follows Unix philosophy: one file, one job.
 *
 * @example
 * const { isExpanded, position, toggle, expand, collapse } = usePanelUIState();
 */

import { useState, useCallback, useMemo, useEffect } from 'react';

// ============================================================================
// Types
// ============================================================================

export type PanelState = 'collapsed' | 'expanded';
export type PanelPosition = 'bottom' | 'right';

export interface UsePanelUIStateOptions {
  /** Default panel state on mount */
  defaultState?: PanelState;
  /** Default panel position on mount */
  defaultPosition?: PanelPosition;
  /** Persist state to localStorage */
  persist?: boolean;
  /** localStorage key prefix */
  storageKey?: string;
}

export interface UsePanelUIStateReturn {
  /** Current panel state */
  panelState: PanelState;
  /** Current panel position */
  panelPosition: PanelPosition;
  /** Whether panel is expanded */
  isExpanded: boolean;
  /** Set panel state directly */
  setPanelState: (state: PanelState) => void;
  /** Set panel position directly */
  setPanelPosition: (position: PanelPosition) => void;
  /** Toggle between collapsed and expanded */
  toggle: () => void;
  /** Expand the panel */
  expand: () => void;
  /** Collapse the panel */
  collapse: () => void;
}

// ============================================================================
// Constants
// ============================================================================

const STORAGE_KEY_STATE = 'solomon-panel-state';
const STORAGE_KEY_POSITION = 'solomon-panel-position';

// ============================================================================
// Helpers
// ============================================================================

function loadFromStorage<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;
  try {
    const stored = localStorage.getItem(key);
    return stored ? (JSON.parse(stored) as T) : defaultValue;
  } catch {
    return defaultValue;
  }
}

function saveToStorage<T>(key: string, value: T): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore localStorage errors
  }
}

// ============================================================================
// Hook
// ============================================================================

export function usePanelUIState({
  defaultState = 'collapsed',
  defaultPosition = 'bottom',
  persist = false,
  storageKey = '',
}: UsePanelUIStateOptions = {}): UsePanelUIStateReturn {
  // Build storage keys with optional prefix
  const stateKey = storageKey ? `${storageKey}-${STORAGE_KEY_STATE}` : STORAGE_KEY_STATE;
  const positionKey = storageKey ? `${storageKey}-${STORAGE_KEY_POSITION}` : STORAGE_KEY_POSITION;

  // Initialize state from localStorage if persist is enabled
  const [panelState, setPanelStateInternal] = useState<PanelState>(() =>
    persist ? loadFromStorage(stateKey, defaultState) : defaultState
  );

  const [panelPosition, setPanelPositionInternal] = useState<PanelPosition>(() =>
    persist ? loadFromStorage(positionKey, defaultPosition) : defaultPosition
  );

  // Persist state changes to localStorage
  useEffect(() => {
    if (persist) {
      saveToStorage(stateKey, panelState);
    }
  }, [persist, stateKey, panelState]);

  useEffect(() => {
    if (persist) {
      saveToStorage(positionKey, panelPosition);
    }
  }, [persist, positionKey, panelPosition]);

  // State setters
  const setPanelState = useCallback((state: PanelState) => {
    setPanelStateInternal(state);
  }, []);

  const setPanelPosition = useCallback((position: PanelPosition) => {
    setPanelPositionInternal(position);
  }, []);

  // Toggle between collapsed and expanded
  const toggle = useCallback(() => {
    setPanelStateInternal((prev) => (prev === 'collapsed' ? 'expanded' : 'collapsed'));
  }, []);

  // Expand the panel
  const expand = useCallback(() => {
    setPanelStateInternal('expanded');
  }, []);

  // Collapse the panel
  const collapse = useCallback(() => {
    setPanelStateInternal('collapsed');
  }, []);

  // Derived state
  const isExpanded = panelState === 'expanded';

  return useMemo(
    () => ({
      panelState,
      panelPosition,
      isExpanded,
      setPanelState,
      setPanelPosition,
      toggle,
      expand,
      collapse,
    }),
    [panelState, panelPosition, isExpanded, setPanelState, setPanelPosition, toggle, expand, collapse]
  );
}

export default usePanelUIState;
